import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { DollarSign, Wallet, FileText } from 'lucide-react';
import type { AppDispatch } from '@/store';
import { clearProgramData } from '@/store/slices/costsSlice';
import { Modal } from '@/components/ui';
import { GlassTabs } from '@/design-system';
import type { Program } from '@/types';
import ProgramBudgetTab from './ProgramBudgetTab';
import CostEntryTable from './CostEntryTable';

interface ProgramBudgetModalProps {
  isOpen: boolean;
  onClose: () => void;
  program: Program | null;
}

const ProgramBudgetModal: React.FC<ProgramBudgetModalProps> = ({
  isOpen,
  onClose,
  program,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [activeTab, setActiveTab] = useState('budget');

  useEffect(() => {
    if (!isOpen) {
      // Clear program cost data when modal closes
      dispatch(clearProgramData());
      setActiveTab('budget');
    }
  }, [isOpen, dispatch]);

  if (!program) return null;

  const tabs = [
    {
      id: 'budget',
      label: 'Budget Settings',
      icon: <Wallet className="w-4 h-4" />,
    },
    {
      id: 'entries',
      label: 'Cost Entries',
      icon: <FileText className="w-4 h-4" />,
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      size="xl"
    >
      <div className="min-h-[600px]">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6 pb-4 border-b border-[var(--border)]">
          <div className="p-3 rounded-xl bg-gradient-to-br from-violet-500/20 to-violet-600/20">
            <DollarSign className="w-6 h-6 text-violet-500" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[var(--text)]">Budget & Costs</h2>
            <p className="text-sm text-[var(--muted)]">{program.name}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <GlassTabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        </div>

        {/* Tab Content */}
        <div className="max-h-[500px] overflow-y-auto pr-2">
          {activeTab === 'budget' && (
            <ProgramBudgetTab
              programId={program.id}
              programName={program.name}
            />
          )}
          {activeTab === 'entries' && (
            <CostEntryTable
              programId={program.id}
              programName={program.name}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ProgramBudgetModal;
